import { Crown, Flame, Gift, Leaf, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import catalogueData from './data/catalogue.json';
import { BottomNav } from './components/BottomNav';
import { DemoTools } from './components/DemoTools';
import { DiamondShop } from './components/DiamondShop';
import { Header, type NavKey } from './components/Header';
import { Hero } from './components/Hero';
import { Player } from './components/Player';
import { SearchOverlay } from './components/SearchOverlay';
import { SeriesDetail } from './components/SeriesDetail';
import { SeriesRow } from './components/SeriesRow';
import { UnlockDialog } from './components/UnlockDialog';
import { clearAnalytics, downloadAnalytics, track } from './analytics';
import { usePersistentState } from './hooks/usePersistentState';
import type { Episode, Series, ViewerState, WalletTransaction } from './types';

const catalogue = (catalogueData as { series: Series[] }).series;
const STORAGE_KEY = 'fc_viewer_state_v1';

const initialState: ViewerState = {
  diamondBalance: 20,
  unlockedEpisodeIds: [],
  myListSeriesIds: ['beneath-palm-shadows', 'cyber-veil'],
  progressByEpisodeId: {
    'beneath-palm-shadows-s01e04': 0.8,
    'cyber-veil-s01e02': 0.45,
    'heir-to-my-heart-s01e03': 0.6,
    'blood-moon-rising-s01e01': 0.3
  },
  completedEpisodeIds: [
    'beneath-palm-shadows-s01e01',
    'beneath-palm-shadows-s01e02',
    'beneath-palm-shadows-s01e03',
    'cyber-veil-s01e01',
    'heir-to-my-heart-s01e01',
    'heir-to-my-heart-s01e02'
  ],
  lastWatchedEpisodeBySeriesId: {
    'beneath-palm-shadows': 'beneath-palm-shadows-s01e04',
    'cyber-veil': 'cyber-veil-s01e02',
    'heir-to-my-heart': 'heir-to-my-heart-s01e03',
    'blood-moon-rising': 'blood-moon-rising-s01e01'
  },
  transactions: [
    {
      id: 'initial-credit',
      type: 'initial-credit',
      amount: 20,
      createdAt: '2026-07-10T12:00:00.000Z',
      description: 'Prototype welcome credit'
    }
  ]
};

interface PlayerSelection {
  series: Series;
  episode: Episode;
}

interface UnlockSelection {
  series: Series;
  episode: Episode;
}

export default function App() {
  const [viewer, setViewer, resetViewer] = usePersistentState<ViewerState>(STORAGE_KEY, initialState);
  const [activeNav, setActiveNav] = useState<NavKey>('home');
  const [heroIndex, setHeroIndex] = useState(0);
  const [selectedSeries, setSelectedSeries] = useState<Series | null>(null);
  const [player, setPlayer] = useState<PlayerSelection | null>(null);
  const [unlockSelection, setUnlockSelection] = useState<UnlockSelection | null>(null);
  const [shopOpen, setShopOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const analyticsMilestones = useRef(new Set<string>());

  const featured = useMemo(() => catalogue.filter((item) => item.featured).slice(0, 6), []);
  const heroSeries = featured[heroIndex % featured.length];

  const progressBySeriesId = useMemo(() => {
    const result: Record<string, { episodeNumber: number; progress: number }> = {};
    for (const item of catalogue) {
      const episodeId = viewer.lastWatchedEpisodeBySeriesId[item.id];
      if (!episodeId) continue;
      const episode = item.episodes.find((candidate) => candidate.id === episodeId);
      if (!episode) continue;
      result[item.id] = {
        episodeNumber: episode.number,
        progress: viewer.progressByEpisodeId[episodeId] ?? 0
      };
    }
    return result;
  }, [viewer.lastWatchedEpisodeBySeriesId, viewer.progressByEpisodeId]);

  const continueWatching = useMemo(
    () => catalogue.filter((item) => Boolean(viewer.lastWatchedEpisodeBySeriesId[item.id])),
    [viewer.lastWatchedEpisodeBySeriesId]
  );
  const trending = useMemo(() => catalogue.filter((item) => !item.genres.includes('Documentary')).slice(0, 8), []);
  const documentaries = useMemo(() => catalogue.filter((item) => item.genres.includes('Documentary')), []);
  const freeToStart = useMemo(() => catalogue.slice().reverse().slice(0, 8), []);
  const myList = useMemo(() => catalogue.filter((item) => viewer.myListSeriesIds.includes(item.id)), [viewer.myListSeriesIds]);

  useEffect(() => {
    track('session_started', { route: activeNav });
  }, []);

  useEffect(() => {
    if (activeNav !== 'home' || selectedSeries || player || searchOpen || shopOpen || toolsOpen) return;
    const interval = window.setInterval(() => setHeroIndex((current) => (current + 1) % featured.length), 9000);
    return () => window.clearInterval(interval);
  }, [activeNav, featured.length, player, searchOpen, selectedSeries, shopOpen, toolsOpen]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setShopOpen(false);
        setToolsOpen(false);
        setUnlockSelection(null);
        setSelectedSeries(null);
        if (player) setPlayer(null);
      }
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        setToolsOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [player]);

  function navigate(key: NavKey) {
    setActiveNav(key);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    track('navigation_selected', { route: key });
  }

  function isEpisodeAvailable(episode: Episode) {
    return episode.free || viewer.unlockedEpisodeIds.includes(episode.id);
  }

  function openSeries(series: Series) {
    setSelectedSeries(series);
    setSearchOpen(false);
    track('series_opened', { seriesId: series.id, title: series.title });
  }

  function selectStartingEpisode(series: Series) {
    const lastEpisodeId = viewer.lastWatchedEpisodeBySeriesId[series.id];
    return series.episodes.find((episode) => episode.id === lastEpisodeId) ?? series.episodes[0];
  }

  function requestPlay(series: Series, episode: Episode) {
    if (!isEpisodeAvailable(episode)) {
      setUnlockSelection({ series, episode });
      track('locked_episode_viewed', { seriesId: series.id, episodeId: episode.id, cost: episode.diamondCost });
      return;
    }
    setPlayer({ series, episode });
    setSelectedSeries(null);
    track('episode_started', { seriesId: series.id, episodeId: episode.id, number: episode.number });
  }

  function playSeries(series: Series) {
    requestPlay(series, selectStartingEpisode(series));
  }

  function confirmUnlock() {
    if (!unlockSelection) return;
    const { series, episode } = unlockSelection;
    if (viewer.diamondBalance < episode.diamondCost) {
      setShopOpen(true);
      return;
    }
    const transaction: WalletTransaction = {
      id: crypto.randomUUID(),
      type: 'episode-unlock',
      amount: -episode.diamondCost,
      createdAt: new Date().toISOString(),
      description: `${series.title} — Episode ${episode.number}`
    };
    setViewer((current) => ({
      ...current,
      diamondBalance: current.diamondBalance - episode.diamondCost,
      unlockedEpisodeIds: Array.from(new Set([...current.unlockedEpisodeIds, episode.id])),
      transactions: [...current.transactions, transaction]
    }));
    setUnlockSelection(null);
    setPlayer({ series, episode });
    track('episode_unlocked', { seriesId: series.id, episodeId: episode.id, cost: episode.diamondCost });
  }

  function purchaseDiamonds(diamonds: number, label: string) {
    const transaction: WalletTransaction = {
      id: crypto.randomUUID(),
      type: 'simulated-purchase',
      amount: diamonds,
      createdAt: new Date().toISOString(),
      description: `${label} package (simulated)`
    };
    setViewer((current) => ({
      ...current,
      diamondBalance: current.diamondBalance + diamonds,
      transactions: [...current.transactions, transaction]
    }));
    setShopOpen(false);
    track('diamond_package_selected', { diamonds, label });
  }

  function toggleMyList(seriesId: string) {
    setViewer((current) => ({
      ...current,
      myListSeriesIds: current.myListSeriesIds.includes(seriesId)
        ? current.myListSeriesIds.filter((id) => id !== seriesId)
        : [...current.myListSeriesIds, seriesId]
    }));
    track('my_list_toggled', { seriesId });
  }

  function updateProgress(episodeId: string, progress: number) {
    const series = catalogue.find((item) => item.episodes.some((episode) => episode.id === episodeId));
    if (!series) return;
    const safeProgress = Math.max(0, Math.min(1, progress));
    setViewer((current) => ({
      ...current,
      progressByEpisodeId: { ...current.progressByEpisodeId, [episodeId]: safeProgress },
      lastWatchedEpisodeBySeriesId: { ...current.lastWatchedEpisodeBySeriesId, [series.id]: episodeId }
    }));

    for (const threshold of [0.25, 0.5, 0.75]) {
      const milestoneKey = `${episodeId}:${threshold}`;
      if (safeProgress >= threshold && !analyticsMilestones.current.has(milestoneKey)) {
        analyticsMilestones.current.add(milestoneKey);
        track(`episode_${threshold * 100}_percent`, { seriesId: series.id, episodeId });
      }
    }
  }

  function completeEpisode(episodeId: string) {
    setViewer((current) => ({
      ...current,
      progressByEpisodeId: { ...current.progressByEpisodeId, [episodeId]: 1 },
      completedEpisodeIds: Array.from(new Set([...current.completedEpisodeIds, episodeId]))
    }));
    track('episode_completed', { episodeId });
  }

  function openShop() {
    setShopOpen(true);
    track('diamond_store_opened', { balance: viewer.diamondBalance });
  }

  function addTestDiamonds() {
    const transaction: WalletTransaction = {
      id: crypto.randomUUID(),
      type: 'manual-credit',
      amount: 20,
      createdAt: new Date().toISOString(),
      description: 'Demo tools credit'
    };
    setViewer((current) => ({
      ...current,
      diamondBalance: current.diamondBalance + 20,
      transactions: [...current.transactions, transaction]
    }));
    track('manual_demo_credit', { amount: 20 });
  }

  function resetDemo() {
    if (!window.confirm('Reset diamonds, unlocks, watch progress, My List and analytics?')) return;
    resetViewer();
    clearAnalytics();
    analyticsMilestones.current.clear();
    setToolsOpen(false);
    setPlayer(null);
    setSelectedSeries(null);
    setUnlockSelection(null);
    setActiveNav('home');
  }

  return (
    <div className="app-shell">
      <Header
        activeNav={activeNav}
        diamondBalance={viewer.diamondBalance}
        mobileMenuOpen={mobileMenuOpen}
        onNavigate={navigate}
        onSearch={() => setSearchOpen(true)}
        onOpenShop={openShop}
        onOpenTools={() => setToolsOpen(true)}
        onToggleMobileMenu={() => setMobileMenuOpen((current) => !current)}
      />

      <main>
        {activeNav === 'home' && (
          <>
            <Hero
              series={heroSeries}
              index={heroIndex}
              count={featured.length}
              isInMyList={viewer.myListSeriesIds.includes(heroSeries.id)}
              onPrevious={() => setHeroIndex((current) => (current - 1 + featured.length) % featured.length)}
              onNext={() => setHeroIndex((current) => (current + 1) % featured.length)}
              onPlay={() => playSeries(heroSeries)}
              onDetails={() => openSeries(heroSeries)}
              onToggleMyList={() => toggleMyList(heroSeries.id)}
              onSelectIndex={setHeroIndex}
            />
            <div className="content-container">
              <SeriesRow title="Continue Watching" series={continueWatching} progressBySeriesId={progressBySeriesId} onOpenSeries={openSeries} onPlaySeries={playSeries} />
              <SeriesRow title="Trending Series" icon={<Flame />} series={trending} onOpenSeries={openSeries} onPlaySeries={playSeries} />
              <SeriesRow title="Free to Start" icon={<Gift />} series={freeToStart} onOpenSeries={openSeries} onPlaySeries={playSeries} />
              <SeriesRow title="Documentaries" icon={<Leaf />} series={documentaries} onOpenSeries={openSeries} onPlaySeries={playSeries} />
              <SeriesRow title="Recommended for You" icon={<Sparkles />} series={catalogue.slice(3, 12)} onOpenSeries={openSeries} onPlaySeries={playSeries} />
            </div>
          </>
        )}

        {activeNav === 'browse' && (
          <CataloguePage title="Browse all series" subtitle="Drama, genre entertainment and documentary storytelling." series={catalogue} onOpenSeries={openSeries} onPlaySeries={playSeries} />
        )}
        {activeNav === 'my-list' && (
          <CataloguePage title="My List" subtitle="Saved locally in this prototype browser." series={myList} onOpenSeries={openSeries} onPlaySeries={playSeries} emptyText="Your list is empty. Add a series from its detail page." />
        )}
        {activeNav === 'continue' && (
          <CataloguePage title="Continue Watching" subtitle="Resume exactly where you stopped." series={continueWatching} progressBySeriesId={progressBySeriesId} onOpenSeries={openSeries} onPlaySeries={playSeries} emptyText="Start an episode to create your continue-watching queue." />
        )}
      </main>

      <BottomNav active={activeNav} onNavigate={navigate} onOpenShop={openShop} onOpenTools={() => setToolsOpen(true)} />

      {selectedSeries && (
        <SeriesDetail
          series={selectedSeries}
          unlockedEpisodeIds={viewer.unlockedEpisodeIds}
          progressByEpisodeId={viewer.progressByEpisodeId}
          isInMyList={viewer.myListSeriesIds.includes(selectedSeries.id)}
          onClose={() => setSelectedSeries(null)}
          onPlayEpisode={requestPlay}
          onUnlockEpisode={(series, episode) => {
            setUnlockSelection({ series, episode });
            track('unlock_clicked', { seriesId: series.id, episodeId: episode.id, cost: episode.diamondCost });
          }}
          onToggleMyList={toggleMyList}
        />
      )}

      {player && (
        <Player
          series={player.series}
          episode={player.episode}
          initialProgress={viewer.progressByEpisodeId[player.episode.id] ?? 0}
          canPlayEpisode={isEpisodeAvailable}
          onClose={() => setPlayer(null)}
          onProgress={updateProgress}
          onComplete={completeEpisode}
          onSelectEpisode={(episode) => {
            setPlayer({ series: player.series, episode });
            track('next_episode_started', { seriesId: player.series.id, episodeId: episode.id });
          }}
          onLockedNext={(episode) => setUnlockSelection({ series: player.series, episode })}
        />
      )}

      {unlockSelection && (
        <UnlockDialog
          series={unlockSelection.series}
          episode={unlockSelection.episode}
          balance={viewer.diamondBalance}
          onClose={() => setUnlockSelection(null)}
          onConfirm={confirmUnlock}
          onOpenShop={() => {
            setUnlockSelection(null);
            openShop();
          }}
        />
      )}

      {shopOpen && <DiamondShop balance={viewer.diamondBalance} onClose={() => setShopOpen(false)} onPurchase={purchaseDiamonds} />}
      {searchOpen && <SearchOverlay series={catalogue} onClose={() => setSearchOpen(false)} onOpenSeries={openSeries} />}
      {toolsOpen && (
        <DemoTools
          balance={viewer.diamondBalance}
          transactions={viewer.transactions}
          onClose={() => setToolsOpen(false)}
          onAddDiamonds={addTestDiamonds}
          onExportAnalytics={downloadAnalytics}
          onReset={resetDemo}
        />
      )}
    </div>
  );
}

interface CataloguePageProps {
  title: string;
  subtitle: string;
  series: Series[];
  progressBySeriesId?: Record<string, { episodeNumber: number; progress: number }>;
  emptyText?: string;
  onOpenSeries: (series: Series) => void;
  onPlaySeries: (series: Series) => void;
}

function CataloguePage({ title, subtitle, series, progressBySeriesId, emptyText, onOpenSeries, onPlaySeries }: CataloguePageProps) {
  return (
    <section className="catalogue-page">
      <div className="catalogue-title">
        <span className="eyebrow"><Crown size={16} /> Fallen Cathedral catalogue</span>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {series.length > 0 ? (
        <div className="catalogue-grid">
          {series.map((item) => (
            <div key={item.id} className="catalogue-grid-item">
              <SeriesRow title="" series={[item]} progressBySeriesId={progressBySeriesId} onOpenSeries={onOpenSeries} onPlaySeries={onPlaySeries} />
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state"><h2>Nothing here yet</h2><p>{emptyText}</p></div>
      )}
    </section>
  );
}
